import React, { useCallback, useEffect, useState } from 'react'
import { CircularLoader as Loader } from '@st-operations/uikit/src/v2'

import Header from '_components/header'
import { getPartners, getPushes } from '_services/api-integrations'
import { getTaxonomies } from '_services/taxonomies'

import { LIST_FIELDS, POLL_EVERY_MS } from './constants'
import PushTable from './push-table'
import './styles.scss'

// Every push made for this taxonomy, newest first. A push that is still going
// shows its progress here rather than making anyone open it, so "is anything
// running?" is answerable at a glance.
const ApiIntegrations = (props) => {
  const { slug } = props.match.params

  const [taxonomy, setTaxonomy] = useState(null)
  const [pushes, setPushes] = useState([])
  // for partner names, and their words for an action
  const [partners, setPartners] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(
    async (taxonomyId) => {
      try {
        const data = await getPushes({
          'taxonomy._id': taxonomyId,
          sort: 'created_at,-1',
          limit: 100,
          fields: LIST_FIELDS,
        })
        setPushes(data?.items || [])
        setError(null)
      } catch (e) {
        setError(e?.message || 'could not load pushes')
      }
    },
    [setPushes]
  )

  useEffect(() => {
    getPartners()
      .then((data) => setPartners(data?.items || []))
      .catch(() => setPartners([]))
  }, [])

  useEffect(() => {
    let cancelled = false

    const start = async () => {
      try {
        const found = await getTaxonomies({ slug, limit: 1 })
        const item = found?.items?.[0]
        if (cancelled) return
        if (!item) {
          setError(`no taxonomy called "${slug}"`)
          setLoading(false)
          return
        }
        setTaxonomy(item)
        await load(item._id)
      } catch (e) {
        if (!cancelled) setError(e?.message || 'could not load the taxonomy')
      }
      if (!cancelled) setLoading(false)
    }

    start()
    return () => {
      cancelled = true
    }
  }, [slug, load])

  // keep the list moving while anything is running, so progress is visible
  // without a refresh
  const anyRunning = pushes.some((push) => push.status === 'running')
  useEffect(() => {
    if (!anyRunning || !taxonomy?._id) return undefined
    const timer = setInterval(() => load(taxonomy._id), POLL_EVERY_MS)
    return () => clearInterval(timer)
  }, [anyRunning, taxonomy, load])

  if (loading) {
    return (
      <div className="api-integrations-loading">
        <Loader />
      </div>
    )
  }

  const base = `/taxonomies/${slug}/api-integrations`

  return (
    <div className="api-integrations">
      {/* the app's fixed top bar, as on the other taxonomy pages */}
      <Header
        title={taxonomy ? `${taxonomy.name} > API Integrations` : 'API Integrations'}
        actions={
          taxonomy
            ? [{ children: 'New push', onClick: () => props.history.push(`${base}/new`) }]
            : []
        }
      />

      <If condition={error}>
        <div className="text-red-600 mb-4">{error}</div>
      </If>

      <If condition={!pushes.length && !error}>
        <div className="text-gray-500">Nothing has been pushed from this taxonomy yet.</div>
      </If>

      <If condition={pushes.length}>
        <PushTable pushes={pushes} partners={partners} linkTo={(push) => `${base}/${push._id}`} />
      </If>
    </div>
  )
}

export default ApiIntegrations
